import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen, RefreshCw, HelpCircle, Wrench } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { searchContent, termsFrom } from "../lib/searchIndex";
import { track } from "../lib/api";

const TYPE_META = {
  Tool: { icon: Wrench, badge: "Tool", colour: "text-[#059669] bg-[#059669]/8 border-[#059669]/20" },
  FAQ: { icon: HelpCircle, badge: "FAQ", colour: "text-[#0F766E] bg-[#0F766E]/8 border-[#0F766E]/20" },
  "First-Time Buyer Guide": { icon: BookOpen, badge: "First-Time Buyer", colour: "text-[#B45309] bg-[#B45309]/8 border-[#B45309]/20" },
  "Remortgage Guide": { icon: RefreshCw, badge: "Remortgage", colour: "text-[#1D4ED8] bg-[#1D4ED8]/8 border-[#1D4ED8]/20" },
};

const POPULAR = [
  { label: "Stamp duty", q: "stamp duty" },
  { label: "Deposit", q: "deposit" },
  { label: "Agreement in Principle", q: "AIP" },
  { label: "Survey", q: "survey" },
  { label: "Conveyancing", q: "conveyancing" },
  { label: "Remortgage", q: "remortgage" },
  { label: "Overpayment", q: "overpayment" },
];

const Highlight = ({ text, terms }) => {
  if (!terms.length) return text;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "ig");
  return text.split(re).map((part, i) =>
    part && terms.includes(part.toLowerCase())
      ? <mark key={i} className="bg-[#10B981]/25 text-inherit rounded-sm">{part}</mark>
      : part
  );
};

export const SearchDialog = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);
  const results = useMemo(() => searchContent(query), [query]);
  const terms = useMemo(() => termsFrom(query), [query]);

  const go = (r) => {
    track("search_result_clicked", { result_type: r.type, result_title: r.title });
    onOpenChange(false);
    navigate(r.href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 sm:max-w-xl overflow-hidden" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search MortgagePath</DialogTitle>
        <DialogDescription className="sr-only">
          Search across MortgagePath's free tools, First-Time Buyer Guide, Remortgage Guide and FAQs. Use the arrow keys or tap to open a result.
        </DialogDescription>
        <div className="flex items-center gap-3 border-b border-[#DCE7E3] px-4">
          <Search className="w-4 h-4 text-[#0F766E] shrink-0" aria-hidden="true" />
          <label htmlFor="mp-search-input" className="sr-only">
            Search MortgagePath guides, FAQs and free tools
          </label>
          <input
            id="mp-search-input"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && results.length) go(results[0]); }}
            placeholder="Search stamp duty, AIP, survey, remortgage…"
            className="w-full py-3.5 pr-6 text-[15px] bg-transparent outline-none placeholder:text-[#94A3B8] text-[#0B1220]"
            data-testid="search-input"
            aria-label="Search MortgagePath guides, FAQs and free tools"
            aria-controls="mp-search-results"
            aria-autocomplete="list"
            role="combobox"
            aria-expanded={results.length > 0}
            type="search"
            autoComplete="off"
          />
        </div>
        <div
          id="mp-search-results"
          role="listbox"
          aria-label="Search results"
          className="max-h-[60vh] overflow-y-auto p-2"
        >
          {query.trim().length <= 1 && (
            <div className="px-4 py-5">
              <div className="text-xs uppercase tracking-widest text-[#0F766E] mb-3">Popular searches</div>
              <div className="flex flex-wrap gap-2">
                {POPULAR.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setQuery(p.q)}
                    data-testid={`search-popular-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-xs font-medium text-[#0F766E] bg-[#0F766E]/8 border border-[#0F766E]/15 rounded-full px-3 py-1.5 hover:bg-[#0F766E]/15 hover:border-[#0F766E]/30 transition-colors"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="mt-4 text-[13px] text-[#94A3B8] leading-relaxed">
                Search across free tools, the First-Time Buyer Guide, the Remortgage Guide and FAQs. Press <kbd className="border border-[#DCE7E3] rounded px-1 py-px text-[10px]">Enter</kbd> to open the top result.
              </p>
            </div>
          )}
          {query.trim().length > 1 && results.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[#94A3B8]" data-testid="search-no-results" aria-live="polite">
              No matches found. Try a different word — e.g. "deposit", "survey" or "remortgage".
            </div>
          )}
          <div aria-live="polite" className="sr-only">
            {results.length > 0 ? `${results.length} result${results.length === 1 ? "" : "s"}` : ""}
          </div>
          {results.map((r) => {
            const m = TYPE_META[r.type];
            const Icon = m.icon;
            return (
              <button
                key={r.href}
                onClick={() => go(r)}
                role="option"
                aria-selected="false"
                data-testid="search-result"
                className="w-full text-left rounded-xl px-4 py-3 hover:bg-[#F7FAF9] focus:bg-[#F7FAF9] outline-none focus:ring-2 focus:ring-[#0F766E]/30 transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-[#0F766E] shrink-0" aria-hidden="true" />
                  <span className="font-sora text-sm font-semibold text-[#0B1220] group-hover:text-[#0F766E] transition-colors">
                    <Highlight text={r.title} terms={terms} />
                  </span>
                  <span className={`ml-auto shrink-0 text-[10px] font-medium uppercase tracking-wider border rounded-full px-2 py-0.5 ${m.colour}`}>
                    {m.badge}
                  </span>
                </div>
                <p className="mt-1 pl-[22px] text-[13px] text-[#475569] leading-relaxed line-clamp-2">
                  <Highlight text={r.snippet} terms={terms} />
                </p>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
